import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import { EVENT_SPECIALIZATIONS, FREELANCE_SPECIALIZATIONS } from "../data/specializations";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Building2, Sparkles, ArrowRight, MapPin, CalendarDays, Info, Plus, Trash2, AlertTriangle } from "lucide-react";
import LocationAutocomplete from "../components/LocationAutocomplete";

export default function PostJob() {
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event_id"); // Pre-linked to an event
  const { jobId } = useParams();
  const isEdit = !!jobId;
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [spec, setSpec] = useState("");
  const [customSpec, setCustomSpec] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", location: "", lat: null, lng: null,
    event_date: "", payment: "", total_slots: 1,
  });
  const [locating, setLocating] = useState(false);
  const nav = useNavigate();

  // Fines
  const [finesEnabled, setFinesEnabled] = useState(false);
  const [useCustomFines, setUseCustomFines] = useState(false);
  const [fineCategories, setFineCategories] = useState([]);
  const [fineNotes, setFineNotes] = useState("");
  const [fineIntroOpen, setFineIntroOpen] = useState(false);

  // Travel allowance
  const [travelExpenseEnabled, setTravelExpenseEnabled] = useState(false);
  const [useCustomTravelExpense, setUseCustomTravelExpense] = useState(false);
  const [travelExpenseRate, setTravelExpenseRate] = useState("");
  const [travelExpenseMinKm, setTravelExpenseMinKm] = useState("");
  const [travelExpenseMaxAmount, setTravelExpenseMaxAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [incompletePopup, setIncompletePopup] = useState(false);
  const [loadingJob, setLoadingJob] = useState(isEdit);
  useEffect(() => {
    if (user.role === "manager" && (!user.company_name || !user.company_name.trim())) {
      setIncompletePopup(true);
    }
  }, [user]);
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/jobs/${jobId}`).then(({ data: j }) => {
      setType(j.opportunity_type);
      setSpec(j.specialization);
      setForm({
        title: j.title || "",
        description: j.description || "",
        location: j.location || "",
        lat: j.lat || null,
        lng: j.lng || null,
        event_date: j.event_date || "",
        payment: j.payment != null ? String(j.payment) : "",
        total_slots: j.total_slots || 1,
      });
      setFinesEnabled(!!j.fines_enabled);
      setUseCustomFines(!!j.use_custom_fines);
      setFineCategories(j.fine_categories || []);
      setFineNotes(j.fine_notes || "");
      setTravelExpenseEnabled(!!j.travel_expense_enabled);
      setUseCustomTravelExpense(!!j.use_custom_travel_expense);
      setTravelExpenseRate(j.travel_expense_rate ?? "");
      setTravelExpenseMinKm(j.travel_expense_min_km ?? "");
      setTravelExpenseMaxAmount(j.travel_expense_max_amount ?? "");
      setStep(3);
      setLoadingJob(false);
    }).catch(() => { toast.error("Failed to load job"); setLoadingJob(false); });
    // eslint-disable-next-line
  }, [jobId]);

  useEffect(() => {
    if (user && (user.role === "manager" || user.role === "freelancer") && !user.seen_rating_intro_jobform) {
      setFineIntroOpen(true);
      api.patch("/users/me", { seen_rating_intro_jobform: true }).then(() => refresh()).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user?.id]);

  const hasManagerDefaults = user?.fine_categories?.length > 0;
  const hasTravelDefault = (user?.travel_expense_rate || 0) > 0;
  const hasOfficeLocation = user?.lat != null && user?.lng != null;

  const addFineCategory = () => setFineCategories(c => [...c, { name: "", rate_per_star: "" }]);
  const updateFineCategory = (i, field, val) => setFineCategories(c => c.map((cat, idx) => idx === i ? { ...cat, [field]: val } : cat));
  const removeFineCategory = (i) => setFineCategories(c => c.filter((_, idx) => idx !== i));

  const useMyLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm(f => ({ ...f, lat: latitude, lng: longitude }));
        // Reverse geocode to get address
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const d = await res.json();
          const addr = d.address;
          const place = [
            addr.amenity || addr.building,
            addr.road,
            addr.suburb || addr.neighbourhood,
            addr.city || addr.town || addr.village,
            addr.state,
          ].filter(Boolean).join(", ");
          setForm(f => ({ ...f, location: place, lat: latitude, lng: longitude }));
          toast.success("Location detected!");
        } catch {
          setForm(f => ({ ...f, location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` }));
        }
        setLocating(false);
      },
      () => { toast.error("Could not get location"); setLocating(false); }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (user.role === "manager" && (!user.company_name || !user.company_name.trim())) {
      setIncompletePopup(true);
      return;
    }
    const finalSpec = showCustom ? customSpec.trim() : spec;
    if (!finalSpec) { toast.error("Please enter a specialization"); return; }
    if (type === "event_staffing") {
      if (!form.location) { toast.error("Location is required"); return; }
      if (!form.lat || !form.lng) { toast.error("Please select a location from the suggestions"); return; }
      if (!form.event_date) { toast.error("Event date is required"); return; }
      if (!form.payment || isNaN(parseFloat(form.payment)) || parseFloat(form.payment) <= 0) { toast.error("Please enter a valid payment amount greater than 0"); return; }
    }
    if (finesEnabled && useCustomFines && !fineNotes.trim()) {
      toast.error("Fine policy notes are required when custom fines are enabled — workers need to know why they're being fined.");
      return;
    }
    try {
      const payload = {
        opportunity_type: type,
        specialization: finalSpec,
        title: form.title,
        description: form.description,
        location: form.location,
        lat: form.lat,
        lng: form.lng,
        event_date: form.event_date || null,
        payment: parseFloat(form.payment),
        total_slots: parseInt(form.total_slots) || 1,
        event_id: eventId || null,
        fines_enabled: type === "event_staffing" ? finesEnabled : false,
        use_custom_fines: useCustomFines,
        fine_categories: useCustomFines ? fineCategories.filter(c => c.name.trim()).map(c => ({ name: c.name.trim(), rate_per_star: Math.max(0, parseFloat(c.rate_per_star) || 0) })) : null,
        fine_notes: useCustomFines ? fineNotes.trim() : null,
        travel_expense_enabled: type === "event_staffing" ? travelExpenseEnabled : false,
        use_custom_travel_expense: useCustomTravelExpense,
        travel_expense_rate: useCustomTravelExpense ? Math.max(0, parseFloat(travelExpenseRate) || 0) : null,
        travel_expense_min_km: useCustomTravelExpense ? Math.max(0, parseFloat(travelExpenseMinKm) || 0) : null,
        travel_expense_max_amount: useCustomTravelExpense ? Math.max(0, parseFloat(travelExpenseMaxAmount) || 0) : null,
      };
      setSubmitting(true);
      if (isEdit) {
        await api.patch(`/jobs/${jobId}`, payload);
        toast.success("Job updated!");
        nav(`/job/${jobId}`);
      } else {
        const { data } = await api.post("/jobs", payload);
        toast.success("Job posted!");
        if (eventId) {
          nav(`/manage-event/${eventId}`);
        } else {
          nav(`/job/${data.id}`);
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

 if (!user) {
    return (
      <div className="mx-auto max-w-md p-12 text-center">
        <p className="font-display text-2xl">Sign in to post a job</p>
        <a href="/login" className="btn-primary mt-4 inline-flex">Sign in</a>
      </div>
    );
   }
  if (isEdit && loadingJob) {
    return <div className="mx-auto max-w-md p-12 text-center text-muted-foreground">Loading job…</div>;
  }
  const specs = type === "event_staffing" ? EVENT_SPECIALIZATIONS : FREELANCE_SPECIALIZATIONS;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12" data-testid="post-job-page">
      {!isEdit && <span className="label-eyebrow text-muted-foreground">Step {step} of 3</span>}
      <h1 className="mt-2 font-display text-4xl tracking-tighter">{isEdit ? "Edit job" : "Post a job"}</h1>

      {step === 1 && (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-opportunity-type">
          {[
            { id: "event_staffing", title: "Event Staffing", desc: "Catering, security, cameramen — physical event gigs.", Icon: Building2 },
            { id: "freelance", title: "Freelance Services", desc: "Skill-based remote work — design, writing, dev.", Icon: Sparkles },
            { id: "create_event", title: "Create an Event", desc: "Post multiple roles under one event — weddings, corporate, etc.", Icon: CalendarDays },
          ].map(o => {
            const sel = type === o.id;
            const I = o.Icon;
            return (
              <button key={o.id} onClick={() => {
                if (o.id === "create_event") { nav("/create-event"); return; }
                setType(o.id); setStep(2);
              }} data-testid={`opp-${o.id}`}
                className={`p-6 text-left border-2 rounded-sm transition ${sel ? "border-brand bg-brand-tint" : "border-slate-200 hover:border-brand hover:bg-brand-tint/40"}`}>
                <I className="w-6 h-6" />
                <p className="mt-3 font-display text-2xl">{o.title}</p>
                <p className="mt-1 text-sm text-slate-600">{o.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs label-eyebrow">Continue <ArrowRight className="w-3 h-3" /></span>
              </button>
            );
          })}
        </div>
      )}

      {step === 2 && (
        <div className="mt-8" data-testid="step-specialization">
          <label className="label-eyebrow text-muted-foreground">Specialization</label>
          {!showCustom ? (
            <>
              <div className="mt-3 flex flex-wrap gap-2">
                {specs.map(s => (
                  <button key={s} onClick={() => { setSpec(s); setShowCustom(false); setStep(3); }} data-testid={`spec-${s}`}
                    className={`px-4 py-2 rounded-sm text-sm border transition ${spec === s ? "bg-brand text-white border-brand" : "bg-background text-foreground border-border hover:bg-brand-tint hover:border-brand"}`}>
                    {s}
                  </button>
                ))}
                <button
                  onClick={() => setShowCustom(true)}
                  className="px-4 py-2 rounded-sm text-sm border border-dashed border-brand text-brand hover:bg-brand-tint transition"
                >
                  + Custom
                </button>
              </div>
            </>
          ) : (
            <div className="mt-3 space-y-3">
              <input
                className="input-base"
                placeholder="Enter your custom specialization…"
                value={customSpec}
                onChange={e => setCustomSpec(e.target.value)}
                autoFocus
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowCustom(false)} className="btn-outline">Back to list</button>
                <button
                  type="button"
                  onClick={() => { if (customSpec.trim()) { setSpec(customSpec.trim()); setStep(3); } else toast.error("Enter a specialization"); }}
                  className="btn-primary"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
          <button onClick={() => setStep(1)} className="btn-outline mt-6">Back</button>
        </div>
      )}

      {step === 3 && (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="step-form">
          <p className="text-sm text-slate-600">
            Posting <strong>{showCustom ? customSpec : spec}</strong> · <span className="capitalize">{type.replace("_", " ")}</span>
          </p>
          <div>
            <label className="label-eyebrow text-muted-foreground">Title</label>
            <input required className="input-base mt-2" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="job-title" />
          </div>
          <div>
            <label className="label-eyebrow text-muted-foreground">Description</label>
            <textarea required rows={5} className="input-base mt-2" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="job-desc" />
          </div>
          {type === "event_staffing" && (
            <>
              <div>
                <label className="label-eyebrow text-muted-foreground">Location <span className="text-red-500">*</span></label>
                <div className="mt-2">
                  <LocationAutocomplete
                    value={form.location}
                    onChange={(val) => setForm({ ...form, location: val, lat: null, lng: null })}
                    onSelect={({ display, lat, lng }) => setForm({ ...form, location: display, lat, lng })}
                    placeholder="Search city, venue or area…"
                    required
                  />
                </div>
                {form.lat && <p className="text-xs text-emerald-600 mt-1">✓ Location confirmed — workers nearby can find this</p>}
                {!form.lat && form.location && <p className="text-xs text-amber-500 mt-1">⚠ Please select a location from the dropdown</p>}
              </div>
              <div>
                <label className="label-eyebrow text-muted-foreground">Event date & time</label>
                <div className="flex gap-2 mt-2">
                  <input
                    type="date"
                    className="input-base flex-1"
                    min={new Date().toISOString().split("T")[0]}
                    value={form.event_date ? form.event_date.split("T")[0] : ""}
                    onChange={e => setForm({ ...form, event_date: e.target.value + (form.event_date ? "T" + form.event_date.split("T")[1] : "T09:00") })}
                    data-testid="job-date"
                  />
                  <input
                    type="time"
                    className="input-base w-36"
                    value={form.event_date ? form.event_date.split("T")[1] || "09:00" : "09:00"}
                    onChange={e => setForm({ ...form, event_date: (form.event_date ? form.event_date.split("T")[0] : new Date().toISOString().split("T")[0]) + "T" + e.target.value })}
                    data-testid="job-time"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Date must be today or later</p>
              </div>
              <div>
                <label className="label-eyebrow text-muted-foreground">Total slots</label>
                <input type="number" min={1} className="input-base mt-2" value={form.total_slots} onChange={e => setForm({ ...form, total_slots: e.target.value })} data-testid="job-slots" />
              </div>
            </>
          )}
        <div>
            <label className="label-eyebrow text-muted-foreground">Payment (₹)</label>
            <input type="number" min={0} step="any" required className="input-base mt-2" value={form.payment} onChange={e => setForm({ ...form, payment: e.target.value })} data-testid="job-payment" />
          </div>

          {type === "event_staffing" && (
            <>
            <div className="border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
                  <input type="checkbox" checked={finesEnabled} onChange={e => setFinesEnabled(e.target.checked)} className="w-4 h-4" />
                  Enable rating fines for this job
                </label>
                <button type="button" onClick={() => setFineIntroOpen(true)} className="text-xs text-brand hover:underline flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" /> What's this?
                </button>
              </div>

              {finesEnabled && (
                <div className="mt-3 space-y-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={useCustomFines} onChange={e => setUseCustomFines(e.target.checked)} className="w-4 h-4" />
                    Use custom fines for this job
                  </label>

                  {!useCustomFines && (
                    <p className="text-xs text-muted-foreground">
                      {hasManagerDefaults
                        ? "Your default rating categories (set in your profile) will apply to this job."
                        : "You haven't set default rating categories yet. Set them in your profile, or check \"Use custom fines for this job\" to define them here."}
                    </p>
                  )}

                  {useCustomFines && (
                    <>
                      <div>
                        <label className="label-eyebrow text-muted-foreground">Star categories</label>
                        <div className="mt-2 space-y-2">
                          {fineCategories.map((cat, i) => (
                            <div key={i} className="flex gap-2 items-center">
                              <input className="input-base !py-2 text-sm min-w-0" style={{ flex: '2 1 0%' }} placeholder="e.g. Uniform"
                                value={cat.name} onChange={e => updateFineCategory(i, "name", e.target.value)} />
                              <span className="text-xs text-muted-foreground">₹</span>
                              <input type="number" min="0" className="input-base !py-2 text-sm" style={{ flex: '0 0 5rem' }} placeholder="per star"
                                value={cat.rate_per_star} onChange={e => updateFineCategory(i, "rate_per_star", e.target.value)} />
                              <button type="button" onClick={() => removeFineCategory(i)} className="text-muted-foreground hover:text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button type="button" onClick={addFineCategory} className="mt-2 text-xs text-brand hover:underline flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" /> Add category
                        </button>
                      </div>

                      <div>
                        <label className="label-eyebrow text-muted-foreground">
                          Fine policy notes <span className="text-red-500">*</span>
                        </label>
                        <textarea rows={3} className="input-base mt-2" placeholder="Explain your rules — e.g. Uniform must be all-black, reporting time is strict…"
                          value={fineNotes} onChange={e => setFineNotes(e.target.value)} />
                        <p className="text-[11px] text-muted-foreground mt-1">Shown to workers on this job listing before they apply.</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="border border-border rounded-xl p-4 mt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
                <input type="checkbox" checked={travelExpenseEnabled} onChange={e => setTravelExpenseEnabled(e.target.checked)} className="w-4 h-4" />
                Enable travel allowance for this job
              </label>

              {travelExpenseEnabled && (
                <div className="mt-3 space-y-4">
                  {!hasOfficeLocation && (
                    <p className="text-xs text-amber-500">
                      ⚠ Set your office location in your profile for accurate travel allowance calculations.
                    </p>
                  )}
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={useCustomTravelExpense} onChange={e => setUseCustomTravelExpense(e.target.checked)} className="w-4 h-4" />
                    Use a custom rate for this job
                  </label>

                  {!useCustomTravelExpense && (
                    <p className="text-xs text-muted-foreground">
                      {hasTravelDefault
                        ? "Your default rate per km (set in your profile) will apply to this job."
                        : "You haven't set a default rate yet. Set it in your profile, or check \"Use a custom rate for this job\" to define one here."}
                    </p>
                  )}

                  {useCustomTravelExpense && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">₹</span>
                        <input type="number" min="0" className="input-base !py-2 text-sm max-w-[8rem]" placeholder="per km"
                          value={travelExpenseRate} onChange={e => setTravelExpenseRate(e.target.value)} />
                        <span className="text-xs text-muted-foreground">per km</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Min.</span>
                        <input type="number" min="0" className="input-base !py-2 text-sm max-w-[8rem]" placeholder="e.g. 3"
                          value={travelExpenseMinKm} onChange={e => setTravelExpenseMinKm(e.target.value)} />
                        <span className="text-xs text-muted-foreground">km to qualify</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Max ₹</span>
                        <input type="number" min="0" className="input-base !py-2 text-sm max-w-[8rem]" placeholder="optional"
                          value={travelExpenseMaxAmount} onChange={e => setTravelExpenseMaxAmount(e.target.value)} />
                        <span className="text-xs text-muted-foreground">cap per worker (optional)</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            {!isEdit && <button type="button" onClick={() => setStep(2)} className="btn-outline">Back</button>}
            <button className="btn-primary flex-1" data-testid="post-job-submit" disabled={submitting}>
              {submitting ? (isEdit ? "Saving…" : "Posting…") : (isEdit ? "Save changes" : "Post job")}
            </button>
          </div>
        </form>
      )}

      {incompletePopup && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="font-display text-xl mb-2">Complete your profile first</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Add your organization name so workers know who's hiring before you post a job.
            </p>
            <div className="flex flex-col gap-2">
              <Link to="/dashboard?tab=settings" className="btn-primary w-full">
                Complete profile
              </Link>
              <button onClick={() => setIncompletePopup(false)} className="btn-outline w-full">
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fine intro popup ── */}
      {fineIntroOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setFineIntroOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">Rating categories & fines</h3>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              This lets you set optional rules to fine workers for issues like uniform or punctuality — deducted from their payment.
            </p>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              <strong>Star categories:</strong> rate a worker 0–5★ on things you define. Each missing star costs the rate you set.
            </p>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              <strong>Delay fines:</strong> automatic brackets based on how late a worker checks in.
            </p>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              Completely optional. Workers always see your fine policy before applying — no hidden fines.
            </p>
            <button onClick={() => setFineIntroOpen(false)} className="btn-primary w-full">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}  
